import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fieldLabel } from "./DiaryTrendChart";
import { uniqueCode } from "./DiaryAddQuestion";

const NUM_KINDS = { max_gte: "Any score at or above", min_lte: "Any score at or below", mean_gte: "Weekly average at or above" };
const WHEN = { max_gte: ["max", "gte"], min_lte: ["min", "lte"], mean_gte: ["mean", "gte"], days_with_gte: ["days_with", "gte"] };

function wording(kind, f, value, opt) {
  const name = fieldLabel(f);
  const unit = f.type === "rating" ? "/10" : f.unit ? ` ${f.unit}` : "";
  if (kind === "days_with_gte") return `“${opt?.label}” on ${value} or more days (${name.toLowerCase()})`;
  if (kind === "mean_gte") return `Average ${name.toLowerCase()} ${value}${unit} or higher over the diary`;
  return `${name} ${f.type === "rating" ? "rated " : ""}${value}${unit} or ${kind === "min_lte" ? "lower" : "higher"}`;
}

/** Inline form that adds one flag rule to a diary draft for any scale, number or choice question. */
export function DiaryAddFlag({ fields, rules, onAdd, onCancel }) {
  const usable = fields.filter((f) => ["rating", "integer", "single"].includes(f.type));
  const [code, setCode] = useState(usable[0]?.code || "");
  const f = usable.find((x) => x.code === code);
  const single = f?.type === "single";
  const [kind, setKind] = useState("max_gte");
  const [option, setOption] = useState("");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const k = single ? "days_with_gte" : kind === "days_with_gte" ? "max_gte" : kind;
  const opt = f?.options?.find((o) => o.value === option);
  const num = value === "" ? null : Number(value);
  const ok = f && num !== null && !Number.isNaN(num) && (!single || (opt && Number.isInteger(num) && num >= 1))
    && (f.type !== "rating" || single || (num >= 0 && num <= 10));
  const auto = ok ? wording(k, f, num, opt) : "";
  function save() {
    const [stat, op] = WHEN[k];
    const taken = new Set(rules.map((r) => r.code));
    onAdd({ code: uniqueCode(`${f.code}_${k}_${num}`, taken), severity: "normal", label: (label || auto).trim(),
            when: { field: f.code, stat, op, value: num, ...(single ? { option: opt.value } : {}) } });
  }
  if (!usable.length) return <p className="text-xs text-zinc-500">Add a 0–10 scale, number or choice question first.</p>;
  return (
    <div className="space-y-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-3" data-testid="diary-add-flag-form">
      <div className="grid gap-2 sm:grid-cols-3">
        <div><Label className="text-xs">Question</Label>
          <Select value={code} onValueChange={(v) => { setCode(v); setOption(""); }}>
            <SelectTrigger className="mt-1 h-8" data-testid="diary-add-flag-field"><SelectValue /></SelectTrigger>
            <SelectContent>{usable.map((x) => <SelectItem key={x.code} value={x.code} data-testid={`diary-add-flag-field-${x.code}`}>{fieldLabel(x)}</SelectItem>)}</SelectContent>
          </Select></div>
        {single ? (
          <div><Label className="text-xs">Answer picked</Label>
            <Select value={option} onValueChange={setOption}>
              <SelectTrigger className="mt-1 h-8" data-testid="diary-add-flag-option"><SelectValue placeholder="Choose an answer" /></SelectTrigger>
              <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`diary-add-flag-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select></div>
        ) : (
          <div><Label className="text-xs">Flag when</Label>
            <Select value={k} onValueChange={setKind}>
              <SelectTrigger className="mt-1 h-8" data-testid="diary-add-flag-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(NUM_KINDS).map(([v, t]) => <SelectItem key={v} value={v} data-testid={`diary-add-flag-kind-${v}`}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        <div><Label className="text-xs">{single ? "On this many days or more" : f?.type === "rating" ? "Level (0–10)" : "Level"}</Label>
          <Input type="number" min={single ? 1 : 0} max={!single && f?.type === "rating" ? 10 : undefined} step={k === "mean_gte" ? "0.5" : "1"}
                 className="mt-1 h-8 font-mono-num" value={value} onChange={(e) => setValue(e.target.value)} data-testid="diary-add-flag-value" /></div>
      </div>
      <div><Label className="text-xs">Flag wording staff see</Label>
        <Input className="mt-1 h-8" value={label} placeholder={auto || "Filled in for you"} onChange={(e) => setLabel(e.target.value)} data-testid="diary-add-flag-label" /></div>
      <div className="flex gap-2">
        <Button size="sm" disabled={!ok} onClick={save} data-testid="diary-add-flag-save">Add flag</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="diary-add-flag-cancel">Cancel</Button>
      </div>
    </div>
  );
}
